// ============================================================
// Mapeamento de padronização — design tokens ÚNICOS e compartilhados do portal
// inteiro. Não é um tema por aba: é um só objeto (`portalTheme`) — um token como
// `tituloCard` vale pra todo card do portal que o usa, não importa a aba. Ao estender
// pra uma nova aba, o token existente é reaproveitado (ou, se o caso de uso for
// realmente novo, cria-se um token novo aqui mesmo, na mesma seção 2) — nunca um
// tema paralelo por aba.
//
// Em uso em: "Carteira" (piloto original — Painel2.jsx, CarteiraValue.jsx,
// SaldoDevedorValue.jsx — só a parte que roda quando a Carteira passa `unit` —, e
// HistoricoLineChart.jsx), "Fluxo de Caixa" (Painel2.jsx — tabela "Movimentações
// Financeiras"/waterfall e tabela "Pagamentos", seção `fluxoCaixa` abaixo) e
// "Enquadramento" (Painel2.jsx/IndicatorValue.jsx, seção `indicadorEnquadramento`
// abaixo). A ideia é estender esses MESMOS tokens pras outras abas do portal
// conforme forem migrando.
//
// Como editar: pra mudar uma cor/tamanho em uso, ache o token semântico certo
// (segunda seção) e troque o valor lá — os componentes só importam `portalTheme`,
// nunca um valor solto. Pra trocar o tom em si (ex.: o que É "azul escuro" no app
// inteiro), mude a `palette` (ela referencia as variáveis CSS globais em
// src/styles/ds/colors.css, então também é reaproveitável fora da Carteira).
//
// Nomenclatura dos tokens semânticos: <aplicacao><Onde>[<Descricao>]. Ex.:
// `tituloTabelaPrincipal` (título, na tabela principal), `tituloTabelaHover` (título,
// na tabela, no hover), `tituloTabelaCardClique` (título, na tabela, no card que abre
// ao clicar), `tituloCard` (título, no card superior — antes da tabela, não é um card
// de clique). "TabelaPrincipal" = a tabela grande com cabeçalho sticky e 1ª coluna
// travada (Emissão/Pu do dia/Qtd Integralizada/... na Carteira; Emissão/Saldo
// Inicial/Entradas/... em Movimentações Financeiras). "Tabela" (sem "Principal") = a
// tabelinha de quebra por série que aparece dentro do hover/card-clique de cada valor
// (só existe hoje na Carteira). Tokens específicos de uma aba (sem equivalente
// genérico) ficam numa seção própria nomeada pela aba, ex. `fluxoCaixa` abaixo.
// ============================================================

// ---- 1) Paleta base — os "nomes das cores" ----
export const palette = {
  azulEscuro: 'var(--brand-indigo)',    // #343e83
  azulClaro: '#3ac1cd',                 // reservado, ainda sem uso na Carteira
  azulIndices: '#5b67c4',               // = GRUPO_META.indices.accent1 (Enquadramento > Razões e Índices)
  azulIndicesEscuro: '#232a5c',         // = GRUPO_META.indices.accent2 (Enquadramento > Razões e Índices)
  azulCr: '#0f6dba',                    // cor do tipo de emissão "CR" (ver `tipoEmissao` abaixo) — mesmo tom usado em PainelHero.jsx (HERO_TIPO, ainda não migrado)
  roxo: 'var(--brand-purple)',          // #9e36ef — ícone de dica, links
  roxoClaro: 'var(--purple-100)',       // #f4e6ff — reservado, ainda sem uso na Carteira
  teal: 'var(--brand-teal)',            // #32becb — legenda de série (3º tom)
  cyan: 'var(--brand-cyan)',            // #00acd8 — legenda de série (4º tom)
  verde: 'var(--success)',              // #8db632 — positivo (entradas, waterfall)
  verdeEscuro: '#5f7d1f',               // texto/valor sobre fundo verde claro (entradas)
  vermelho: 'var(--danger)',            // #ff4848 — negativo (saídas, waterfall)
  vermelhoEscuro: '#a31f1f',            // texto/valor sobre fundo vermelho claro (saídas)
  roxoEscuro: '#5c1a90',                // texto/valor sobre fundo roxo claro (despesas)
  cinzaEscuro: 'var(--gray-700)',       // #4a525a
  cinzaMedio: 'var(--gray-600)',        // #717e86
  cinzaMedioClaro: 'var(--gray-500)',   // #8d979e
  cinzaClaro: 'var(--gray-400)',        // #adb0b2
  cinzaMuitoClaro: 'var(--gray-300)',   // #d7dadd
  cinzaBorda: 'var(--gray-100)',        // #eff0f1
  tintaPrincipal: 'var(--ink)',         // #231f20 — texto padrão (quase preto)
  branco: '#ffffff',
};

// ---- 2) Tokens semânticos — o que cada cor/tamanho representa no portal ----
export const portalTheme = {
  // ── Tabela principal (Emissão / Pu do dia / Qtd Integralizada / Valor Emitido /
  // Valor a Emitir / Saldo Devedor) ──
  tituloTabelaPrincipal: { color: palette.azulEscuro },                          // cabeçalho das colunas
  subtituloTabelaPrincipal: { color: palette.cinzaClaro, fontSize: 10 },         // "R$"/"cotas" abaixo do cabeçalho
  nomeEmissaoTabelaPrincipal: { color: palette.azulEscuro, fontSize: 12 },       // nome/código da emissão (1ª coluna)
  valorTabelaPrincipalEmissao: { color: palette.tintaPrincipal, fontSize: 12 },  // valor de cada célula, linha da emissão
  valorTabelaPrincipalSerie: { color: palette.cinzaEscuro, fontSize: 10 },       // valor de cada célula, linha da série expandida
  fundoTabelaPrincipalSerie: 'rgba(52,62,131,0.03)',                            // fundo tingido da linha de série expandida
  fundoTabelaPrincipalCabecalho: palette.branco,                                // fundo do cabeçalho (sticky, cobre o conteúdo ao rolar)
  fundoTabelaPrincipalZebra: '#fafbfe',                                         // fundo das linhas pares (zebra), 1ª coluna sticky inclusive
  bordaTabela: palette.cinzaBorda,                                              // linha fina de separação (rodapés, divisores)

  // ── Tabela de quebra por série (Série · Qtd/PU · Valor unit. · Valor) — aparece
  // no hover (compacto) E no card aberto ao clicar (maior, "CardClique") ──
  tituloTabelaHover: { color: palette.azulEscuro, fontSize: 10 },                // rótulo acima da tabelinha, no hover (ex. "Valor Emitido por série")
  tituloTabelaCardClique: { fontSize: 22 },                                     // título do card (h3), ao clicar (ValorSerieModal/QtdIntegralizadaModal)
  subtituloTabela: { color: palette.cinzaClaro, fontSize: 11 },                  // "Valores em R$/cotas" — igual no hover e no card-clique
  cabecalhoColunaTabelaHover: { color: palette.cinzaEscuro, fontSize: 11.5 },    // cabeçalho da tabelinha, no hover
  cabecalhoColunaTabelaCardClique: { color: palette.cinzaEscuro, fontSize: 13 }, // cabeçalho da tabelinha, no card-clique
  nomeSerieTabelaHover: { color: palette.cinzaEscuro, fontSize: 11.5 },          // coluna "Série", no hover
  nomeSerieTabelaCardClique: { color: palette.cinzaEscuro, fontSize: 13 },       // coluna "Série", no card-clique
  celulaSecundariaTabelaHover: { color: palette.cinzaMedio, fontSize: 11.5 },    // colunas PU/Qtd. + rótulo "Total", no hover
  celulaSecundariaTabelaCardClique: { color: palette.cinzaMedio, fontSize: 13 },// colunas PU/Qtd. + rótulo "Total", no card-clique
  valorDestaqueTabelaHover: { color: palette.azulEscuro, fontSize: 11.5 },       // coluna "Valor"/"PU" em destaque + total, no hover
  valorDestaqueTabelaCardClique: { color: palette.azulEscuro, fontSize: 13 },   // coluna "Valor"/"PU" em destaque + total, no card-clique

  // "—" quando não há dado apurado ainda pra aquela emissão/período.
  placeholderVazio: { color: palette.cinzaMuitoClaro },

  // ── Texto de entrada da aba (TituloIntroAba, Painel2.jsx) — eyebrow
  // (".lev-eyebrow2", rótulo curto, mesmo nome da aba no menu) + título (h3, a
  // manchete de verdade) + parágrafo descritivo ("corpo"). Mesmo padrão
  // eyebrow+h3 usado em todo o portal (modais, card-clique etc. — ver
  // `.lev-eyebrow2` em lev.css), só que aqui o "card" é a própria aba. Antes só
  // tinha o eyebrow sozinho fazendo de título (por isso `corpo` já existia, mas
  // não `titulo`) — ficava com peso pequeno demais pra abrir uma aba inteira. O
  // TEXTO de cada título/eyebrow fica em `textosIntroAba` (seção 3 abaixo), não
  // aqui — aqui é só o estilo. As dicas de uso logo abaixo (ex. "Passe o mouse
  // sobre um valor...") não usam mais um token próprio — são o componente
  // `DicasUso` (src/components/UI/index.jsx), que já reaproveita `dicaCard`/
  // `iconeDicaCard` (mesmo padrão visual do InfoDica). Hoje as abas Carteira e
  // Enquadramento (Painel2.jsx) e a Validação de PU (Interno/ValidacaoPu.jsx)
  // usam; genérico o bastante pra outras abas reaproveitarem quando tiverem o
  // mesmo padrão.
  introAba: {
    titulo: { color: palette.azulEscuro, fontSize: 22 },
    corpo: { color: palette.cinzaMedio, fontSize: 13, lineHeight: 1.5 },
  },

  // ── Cards de resumo do topo (Qtd Integralizada, Valor Emitido, Valor a Emitir,
  // Saldo Devedor) — e o card grande que abre ao clicar num deles ("CardClique") ──
  tituloCard: { color: palette.azulEscuro, fontSize: 16 },                       // título do card superior (não é o de clique)
  valorCard: { color: palette.tintaPrincipal, fontSizeMax: 24, fontSizeMin: 10 },// valor do card superior (encolhe conforme o espaço)
  legendaCard: { color: palette.cinzaMedioClaro, fontSize: 12.5 },               // "n emissões"
  dotCard: { color: palette.azulIndices },                                      // bolinha ao lado do título, no card superior — mesma cor do card de Razões e Índices (Enquadramento)
  hoverCard: { c1: palette.azulIndices, c2: palette.azulIndicesEscuro },         // --lev-card-hover-c1/-c2 (faixa vertical ao passar o mouse) — idem, mesmo gradiente do card de Razões e Índices
  tituloCardClique: { color: palette.azulEscuro, fontSize: 21 },                 // título do card que abre ao clicar no card superior
  subtituloCardClique: { color: palette.cinzaMedioClaro, fontSize: 12.5 },       // linha de período abaixo do título, no card-clique
  valorCardClique: { color: palette.azulEscuro, fontSize: 26 },                  // valor em destaque, no card-clique

  // ── Gráfico de linhas — usado nos cards e nas tabelas ──
  linhaGrafico: { color: palette.azulEscuro },                                  // sempre azul escuro, sem variação por série
  eixoGrafico: { color: palette.cinzaClaro, fontSize: 9 },                       // rótulos de mês no eixo
  tooltipGrafico: { fundo: palette.tintaPrincipal, texto: palette.branco, fontSize: 10 }, // balão de valor ao passar o mouse num ponto

  // ── Painel flutuante do hover (fundo + sombra) ──
  fundoPainelHover: palette.branco,
  sombraPainelHover: 'rgba(52,62,131,.22)',

  // Texto explicativo que substitui o gráfico de série histórica em cards
  // superiores da Carteira (ver `textosCardCarteira` abaixo) — mesmo hover/clique
  // do gráfico, só que com uma explicação no lugar. `dicaCard` é a segunda linha
  // (com ícone de dica), quando o texto aponta pra outro lugar do portal.
  textoExplicativoCard: { color: palette.cinzaMedio, fontSize: 12.5, lineHeight: 1.5 },
  dicaCard: { color: palette.cinzaMedioClaro, fontSize: 11.5, lineHeight: 1.4, fontStyle: 'italic' },
  iconeDicaCard: { color: palette.roxo },                                       // ícone de lâmpada ao lado do texto de `dicaCard`

  // ══════════════════════════════════════════════════════════════════════
  // Modelo "Card e hover de indicador por série" — Série + N colunas de valor
  // (Qtd Integralizada e Pu do dia: 0 colunas extras; Valor Emitido/a Emitir: Qtd +
  // Valor unit.; Saldo Devedor: PU + Qtd.) — ver IndicadorPorSerieValue/Modal/Tabela
  // em CarteiraValue.jsx. Baseado no protótipo `padrao-hover-card-indicador.html`.
  // Único ponto de configuração — mudar aqui reflete em todas as colunas que
  // seguem o modelo, tanto no hover quanto no card. Só o Saldo Devedor usa a
  // variante com gráfico (seção `grafico` abaixo) — as demais não têm série
  // histórica nesse modelo.
  // ══════════════════════════════════════════════════════════════════════
  indicadorPorSerie: {
    // Larguras
    hoverMinWidth: 300, hoverMaxWidth: 380,
    modalMinWidth: 300, modalMaxWidth: 560,

    // ── Hover (painel flutuante, ao passar o mouse na célula) — nunca tem
    // gráfico, mesmo quando o card do mesmo indicador tem (ver `grafico`) ──
    hoverEyebrow: { color: palette.azulClaro, fontSize: 10 },              // "Carteira · Qtd Integralizada"
    hoverTitulo: { color: palette.azulEscuro, fontSize: 14 },                    // nome da emissão (+ série, se expandida)
    hoverSubtitulo: { color: palette.cinzaMedio, fontSize: 11, lineHeight: 1.45 },// texto explicativo (textosCardCarteira)
    hoverHero: { color: palette.tintaPrincipal, fontSize: 21 },                  // valor consolidado, em destaque
    hoverHeroUnidade: { color: palette.cinzaMedioClaro, fontSize: 11 },          // "R$" antes do hero, só quando `unit` é moeda
    hoverDateline: { color: palette.cinzaMedioClaro, fontSize: 10.5 },           // "N séries · em cotas/valores em R$ · <período>"
    // (a dica, no fim do hover/card, reaproveita `dicaCard`/`iconeDicaCard` — mesmo
    // estilo de texto/ícone em todo o portal, ver InfoDica.)

    // ── Card (modal, ao clicar na célula) ──
    modalTitulo: { color: palette.azulEscuro, fontSize: 24 },                    // nome da emissão (+ série) — h3
    modalSubtitulo: { color: palette.cinzaMedio, fontSize: 13, lineHeight: 1.5 },// texto explicativo (textosCardCarteira)
    modalDateline: { color: palette.cinzaMedioClaro, fontSize: 12.5 },           // "N séries · em cotas/valores em R$ · <período>"
    modalHero: { color: palette.tintaPrincipal, fontSize: 38 },                  // valor consolidado, bem grande
    modalHeroUnidade: { color: palette.cinzaClaro, fontSize: 15 },               // "R$" antes do hero, só quando `unit` é moeda
    modalHeroCap: { color: palette.cinzaMedio, fontSize: 12 },                   // legenda abaixo do hero ("... — soma das séries")
    modalStackHead: { color: palette.cinzaMedio, fontSize: 11 },                 // "Série histórica · N meses" (cabeçalho do gráfico, no card)
    modalSecaoTabela: { color: palette.cinzaMedio, fontSize: 11 },               // "Quebra por série"

    // ── Tabela de quebra por série — reaproveita os tokens de
    // cabecalhoColunaTabelaHover/CardClique etc. (seção acima) pras cores de
    // texto; aqui só o que é específico do modelo (coluna "Part.%").
    participacaoHover: { color: palette.cinzaMedioClaro, fontSize: 11.5 },
    participacaoModal: { color: palette.cinzaMedioClaro, fontSize: 13.5 },

    // ── Gráfico de série histórica — só no card (nunca no hover), só quando o
    // indicador tem `hist`. Hoje só o Saldo Devedor usa. Gradiente roxo→teal
    // (mesmo da marca), igual ao antigo `linhaGrafico`, mas em área preenchida.
    grafico: {
      linhaInicio: palette.roxo, linhaFim: palette.teal,                        // gradiente do traço e da área
      wrapBackground: 'linear-gradient(180deg,#fbfcfe,#f6f8fc)', wrapBorderRadius: 14,
      mesLabel: { color: palette.cinzaMedioClaro, fontSize: 9 },
      chipAlta: { color: '#5f7d16', background: 'rgba(141,182,50,.14)' },        // variação positiva no mês
      chipBaixa: { color: '#cf6b63', background: 'rgba(207,107,99,.13)' },       // variação negativa no mês
    },
  },

  // ══════════════════════════════════════════════════════════════════════
  // Enquadramento (Painel2.jsx > IndicatorValue.jsx) — hover (HoverPreview) e
  // card (IndicatorModal) do valor apurado de cada fundo/razão-índice, por
  // série. Mesma disciplina de padronização do modelo "indicadorPorSerie"
  // acima (eyebrow/rótulos em destaque + hero), mas token à parte: aqui não
  // existe "hero = soma das séries" — cada série apura seu próprio valor e
  // referência, consolidar não faz sentido — então o hover/card é outro
  // componente (usa MiniSpark, não AreaChartSVG; painel/wrapper reaproveitados
  // de HoverAnchor/HoverPanel, exportados de CarteiraValue.jsx). O valor em si
  // (bolinha + número) sempre usa STATUS (components/UI/index.jsx), nunca esta
  // paleta — aqui só o que é estrutural (rótulos, cabeçalhos, texto de apoio).
  // ══════════════════════════════════════════════════════════════════════
  indicadorEnquadramento: {
    // ── Hover (HoverPreview, IndicatorValue.jsx) ──
    hoverEyebrow: { color: palette.azulClaro, fontSize: 10 },          // "Enquadramento · Fundo" / "Enquadramento · Razão e Índice"
    hoverTitulo: { color: palette.azulEscuro, fontSize: 14 },          // nome do indicador (+ série, se a célula tiver mais de uma)
    hoverGraficoHead: { color: palette.cinzaClaro, fontSize: 10 },      // "Últimos N meses" / cabeçalho do bloco do campo secundário
    hoverDeltaLabel: { color: palette.cinzaMedioClaro, fontSize: 11 }, // "vs. mês anterior" / nome do campo, ao lado do delta
    hoverDeltaVazio: { color: palette.cinzaClaro },                    // "—" quando não dá pra calcular o delta

    // ── Card (IndicatorModal, Painel2.jsx) ──
    modalTitulo: { color: palette.azulEscuro, fontSize: 21 },          // nome do indicador (h3) — mesmo azul de `indicadorPorSerie.modalTitulo`
    modalValor: { fontSize: 22 },                                      // valor apurado em destaque, dentro do kv-box — cor por STATUS
    modalLabel: { color: palette.cinzaMedio, fontSize: 13 },           // "Referência" / "Variação vs. mês anterior"
    modalRodape: { color: palette.cinzaMedioClaro, fontSize: 12, lineHeight: 1.6 }, // texto final (período + regra de monitoramento)
  },

  // ── Composição por tipo de emissão (CRI/CRA/CR/Debênture) — cor + ícone por
  // classe de ativo, usada onde a carteira acompanhada é resumida numa barra
  // segmentada + legenda (hoje: Welcome.jsx). Mesma paleta de PainelHero.jsx
  // (HERO_TIPO, ainda não migrado pra portalTheme — canonizada aqui pra não
  // nascer um terceiro mapeamento paralelo; ao migrar o Painel, ele passa a
  // importar daqui também).
  tipoEmissaoOrder: ['CRI', 'CRA', 'CR', 'Debênture'],
  tipoEmissao: {
    CRI:          { color: palette.azulEscuro,  icon: 'building-2' },
    CRA:          { color: palette.verdeEscuro, icon: 'wheat' },
    CR:           { color: palette.azulCr,      icon: 'file-text' },
    'Debênture': { color: palette.roxo,        icon: 'landmark' },
  },

  // ══════════════════════════════════════════════════════════════════════
  // Fluxo de Caixa — tabela "Movimentações Financeiras" + waterfall
  // (MovValorHover/MovimentacoesModal/WaterfallSVG/buildWaterfallSteps, todos em
  // Painel2.jsx) e tabela "Pagamentos" (PagamentoGroupRows). A tabela de
  // Movimentações é uma "TabelaPrincipal" (cabeçalho/zebra/borda — tokens
  // genéricos da seção acima, reaproveitados); aqui só o que é específico do
  // fluxo financeiro, sem equivalente em outra aba.
  // ══════════════════════════════════════════════════════════════════════
  fluxoCaixa: {
    // Linha da emissão na tabela de Movimentações — nome (1ª coluna) e valor
    // agregado (Saldo Inicial/Entradas/Saídas/Saldo Final, ver MovValorHover).
    movimentacoes: { nomeEmissao: palette.azulEscuro, valor: palette.azulEscuro, valorZero: palette.cinzaMuitoClaro },

    // Sub-linhas expandidas da tabela de Movimentações — o que compõe Entradas/
    // Saídas/Despesas ("Expandir detalhes"), uma cor por categoria (despesa
    // soma-se a Saídas, ver buildMovimentacoes em relatorioModel.js, mas tem fundo
    // próprio pra diferenciar da saída operacional).
    entrada: { color: palette.verdeEscuro, fundo: 'rgba(141,182,50,.05)' },
    saida: { color: palette.vermelhoEscuro, fundo: 'rgba(255,72,72,.05)' },
    despesa: { color: palette.roxoEscuro, fundo: 'rgba(158,54,239,.05)' },

    // Waterfall Início → Entradas → Saídas → Saldo Final — no preview de hover
    // (pequeno) e no card ao clicar (grande), mesmo componente (WaterfallSVG).
    waterfall: {
      inicio: palette.cinzaClaro, entradas: palette.verde, saidas: palette.vermelho, saldoFinal: palette.azulEscuro,
      valor: { color: palette.cinzaMedio, fontSize: 10 },       // valor acima de cada barra
      categoria: { color: palette.cinzaClaro, fontSize: 9.5 },  // rótulo abaixo de cada barra
    },

    // Tabela "Pagamentos" (Emissão · Qtd (d-1) · Juros · Amortização · A.
    // Extraordinária · Prêmio · Total · Status) — linha da emissão e linha da
    // série expandida (PagamentoGroupRows) seguem a mesma paleta.
    pagamentos: {
      nomeEmissao: palette.azulEscuro, subLabel: palette.cinzaMedio,
      valorPadrao: palette.cinzaMedio,                          // Qtd (d-1)
      amortizacao: palette.teal, premio: palette.roxo,
      valorZero: palette.cinzaMuitoClaro,                       // amort./prêmio == 0 ("—")
      total: palette.azulEscuro,
    },
  },

  // ══════════════════════════════════════════════════════════════════════
  // Validação de PU — tabela "Validação de PU × Agente Fiduciário"
  // (PuValidacaoGroupRows, Interno/ValidacaoPu.jsx): linha de cabeçalho por emissão + uma
  // linha por split. Mesmo modelo estrutural da tabela "Pagamentos" (seção
  // `fluxoCaixa` acima) — sem cabeçalho sticky nem 1ª coluna travada, por isso
  // token próprio em vez de reaproveitar "TabelaPrincipal" (Carteira/
  // Movimentações). O status (Igual/Divergente/Sem verificação) não mora aqui
  // — é o badge `PuStatusPill`/`STATUS`, em components/UI/index.jsx (paleta de
  // pill, não de tabela) — mas a cor da coluna "Diferença" segue o mesmo
  // critério (`diferencaIgual`/`diferencaDivergente` abaixo, não o sinal do
  // valor: uma diferença "Divergente" fica vermelha pra mais ou pra menos).
  // ══════════════════════════════════════════════════════════════════════
  puValidacao: {
    nomeEmissao: palette.azulEscuro, subLabel: palette.cinzaMedioClaro,
    agenteCabecalho: palette.cinzaClaro,                        // nome do agente fiduciário, ao lado do nome da emissão (1 por emissão)
    split: palette.cinzaEscuro,                                 // coluna "Série" (nome + código IF)
    valor: palette.tintaPrincipal,                              // colunas "PU Nosso"/"PU Agente"
    diferencaIgual: palette.verdeEscuro,                        // status "Igual" — verde, não importa o sinal
    diferencaDivergente: palette.vermelhoEscuro,                // status "Divergente" — vermelho, pra mais ou pra menos
    diferencaNeutra: palette.cinzaMedioClaro,                   // status "Sem verificação" (sem PU do agente)
  },

  // ══════════════════════════════════════════════════════════════════════
  // Série inativa em Validação de PU (Interno/ValidacaoPu.jsx) — tabela e
  // ranking de diferenças. Não esconde a série (o dado de validação continua
  // útil), só desbota a linha/item inteiro e troca o rótulo da série por um
  // selo cinza "Inativa", pro caso do desbotado sozinho passar despercebido.
  // Mesmo `sp.ativo` já usado em SerieCard.jsx/Detalhe2.jsx.
  // ══════════════════════════════════════════════════════════════════════
  serieInativa: {
    opacity: 0.45,
    corSelo: palette.cinzaMedioClaro,
    fundoSelo: palette.cinzaBorda,
  },

  // ══════════════════════════════════════════════════════════════════════
  // Destaque de série por filtro de código IF — padrão único pras duas telas
  // que têm um filtro "Filtrar código IF" além do de emissão: Carteira
  // (Painel2.jsx, tabela principal, `carteiraCodigoIfFilter`) e Validação de PU
  // (Interno/ValidacaoPu.jsx, `codigoIfFilter`, PuValidacaoGroupRows — modo
  // agrupado por emissão; o modo achatado por diferença, `diffSort`, continua
  // escondendo a série que não bate, por ser uma lista já sem agrupamento pra
  // preservar as "irmãs"). Regra: com o filtro ativo, (1) a emissão some da
  // tabela só se NENHUMA das suas séries bater — nunca esconde uma série
  // individualmente; (2) todas as séries continuam visíveis (a Carteira
  // também expande automaticamente as linhas de série nesse caso, ver
  // `linhaExpandida` em Painel2.jsx — a Validação de PU não tem conceito de
  // linha expandida, toda série já é sempre uma linha própria); (3) a(s)
  // série(s) cujo `codigo_if` está selecionado no filtro ganham os tons abaixo.
  // ══════════════════════════════════════════════════════════════════════
  destaqueFiltroSerie: {
    fundoLinha: 'rgba(52,62,131,0.1)',   // fundo da <tr> da série destacada (mais forte que o fundo normal de série)
    fundoColunaSticky: '#eaecf8',         // fundo da 1ª coluna, só onde ela é sticky (Carteira) — mesma família de tom
    bordaAccent: palette.azulEscuro,      // borda esquerda (box-shadow inset) na 1ª coluna/célula da série destacada
    textoCodigoIf: palette.azulEscuro,    // cor do código IF em si, quando destacado (substitui o cinza padrão)
  },

  // ══════════════════════════════════════════════════════════════════════
  // Modelo "Card de distribuição" (CardDistribuicao, Interno/ValidacaoPu.jsx) — dot +
  // título, hero em destaque (badge % pro item "ruim", ou uma contagem simples
  // quando não há polaridade boa/ruim) + barra segmentada e legenda clicável
  // que filtra a tabela abaixo. É o card "SEM tabela": a própria legenda já
  // funciona como a quebra por item (nome + contagem + %) — o card "COM
  // tabela" é o modelo "indicadorPorSerie" acima (abre num hover/card maior com
  // uma IndicadorPorSerieTabela de verdade, ver CarteiraValue.jsx). Baseado no
  // visual dos cards de resumo de Enquadramento (EnquadramentoSummaryCard,
  // Painel2.jsx — hoje também usa este token: `heroValor`/`heroLabel`/
  // `corNegativaTexto`/`corNeutraTexto` pro hero em %, `tituloCard`/`legendaCard`
  // acima pro resto do cabeçalho). Além dele, a Validação de PU usa (cards
  // "Qtd de Emissões"/"Agentes Fiduciários"/
  // "Frequência de Status"), mas o token é genérico — qualquer card com o mesmo
  // desenho reaproveita, não é específico de PU. Único ponto de configuração —
  // mudar aqui reflete em todos os cards que seguem o modelo. `corPositiva`/
  // `corNegativa`/`corNeutra` em hex literal (não var()), igual `CHART_HEX`
  // (chartColors.js): `hexToRgba` (Interno/ValidacaoPu.jsx) precisa de hex pra
  // destacar o item selecionado na legenda.
  // ══════════════════════════════════════════════════════════════════════
  cardDistribuicao: {
    dot: { color: palette.azulIndices },                                 // bolinha ao lado do título — mesmo azul de `dotCard` (cards de resumo da Carteira)
    titulo: { color: palette.azulEscuro, fontSize: 16 },
    subtitulo: { color: palette.cinzaMedioClaro, fontSize: 12 },
    hover: { c1: palette.azulIndices, c2: palette.azulIndicesEscuro },    // --lev-card-hover-c1/-c2 — mesmo gradiente de `hoverCard` acima
    heroValor: { fontSize: 20 },                                         // cor vem de `corNegativaTexto`/`corNeutraTexto` conforme o caso
    heroLabel: { color: palette.cinzaMedioClaro, fontSize: 10 },
    corPositiva: '#8db632',                        // item "bom" (ex. "Com Validação"/"Igual") — = STATUS.enquadrado.dot (components/UI/index.jsx)
    corNegativa: '#ff4848',                         // item "ruim" (ex. "Sem Validação"/"Divergente") — = STATUS.desenquadrado.dot
    corNeutra: '#adb0b2',                           // item neutro (ex. "Sem verificação") — = CHART_HEX.gray400 (chartColors.js) / STATUS.neutro
    corNegativaTexto: '#d63333',                     // hero em destaque, quando o item "ruim" está presente — = STATUS.desenquadrado.color
    corNeutraTexto: palette.cinzaMedioClaro,         // hero quando não há item "ruim" (ou métrica sem polaridade, ex. nº de agentes)
    corDestaqueTexto: palette.tintaPrincipal,        // nome do item "ruim", na legenda — preto e negrito, NUNCA colorido (só a bolinha e o % ficam na cor do status)
    barraFundo: palette.cinzaBorda,
    legenda: { color: palette.cinzaMedio, fontSize: 12.5 },
    legendaValor: { color: palette.azulEscuro },
    legendaPct: { color: palette.cinzaMedioClaro },
    dica: { color: palette.cinzaClaro, fontSize: 11 },
    vazio: { color: palette.cinzaClaro, fontSize: 12 },
  },

  // ══════════════════════════════════════════════════════════════════════
  // SeletorPill (components/UI/index.jsx) — grupo de opção única (período,
  // faixa de gráfico, tipo de emissão, operador de filtro...) num trilho só,
  // com o selecionado virando um "pill" que desliza até a nova posição.
  // Trilho com sombra PRA DENTRO (afundado); pill com sombra normal (pra
  // fora), pra ler como "acima" do trilho. Piloto: CarteiraPeriodoSeletor
  // (Painel2.jsx, aba Carteira) — depois virou o padrão de qualquer seletor
  // de opção única mutuamente exclusiva do portal (ver SeletorPill).
  // ══════════════════════════════════════════════════════════════════════
  seletorPill: {
    trackBackground: palette.branco,
    trackShadow: 'inset 0 1px 4px rgba(0,0,0,.10), inset 0 0 0 1px rgba(0,0,0,.045)',
    pillBackground: palette.azulEscuro,
    pillShadow: '0 2px 8px rgba(52,62,131,.25)',
    textoSelecionado: palette.branco,
    textoNormal: palette.azulEscuro,
    subSelecionado: 'rgba(255,255,255,.72)',
    subNormal: palette.cinzaClaro,
    transicao: 'left .32s cubic-bezier(.4,0,.2,1), width .32s cubic-bezier(.4,0,.2,1)',
    // Padding do trilho + dos botões — calibrado pra fechar em ~36-38px de
    // altura total, mesma altura de MultiSelectSearchFilter (`height:36`) e
    // do ExpandBtn (`.lev-chip`), pra ficar visualmente alinhado quando o
    // seletor aparece ao lado de outros filtros na mesma barra.
    trackPadding: 3,
    pillPaddingComSub: '4px 12px',   // opções com 2 linhas (label + sub, ex. período)
    pillPaddingSemSub: '8px 15px',   // opções de 1 linha só (ex. "3 meses", "CRI")
    fontSizeLabel: 12,                // label principal (ex. "Mar/26", "Hoje", "3 meses")
    fontSizeSub: 9,                   // 2ª linha, quando existe (ex. "m-3", "agora")
  },

  // ══════════════════════════════════════════════════════════════════════
  // Documentos (Detalhe2.jsx > DocumentosTab) — os 4 boxes por tipo
  // (Documentos/Comunicados/Relatórios/Assembleia) e a lista de arquivos
  // (PdfRow) dentro de cada um. Título do box e contagem "N arquivos"
  // reaproveitam `tituloCard`/`legendaCard` (mesmo padrão dos cards de
  // resumo da Carteira — aqui o "dot" vira um ícone por tipo, ver DOCS_META,
  // mas a cor/tamanho do título e da legenda é o mesmo token). Só o nome do
  // arquivo em si, dentro do PdfRow, é específico daqui — sem equivalente em
  // outra aba ainda.
  // ══════════════════════════════════════════════════════════════════════
  documentos: {
    nomeArquivo: { color: palette.cinzaMedio, fontSize: 13 }, // nome do arquivo, no PdfRow
  },

  // ══════════════════════════════════════════════════════════════════════
  // Modelo "Card de escolha" — grade de botões-card pra entrar em algo (ícone
  // ou logo + título + descrição + CTA "Abrir"/"Acessar"), listra de hover
  // (.lev-card-hover). Hoje em uso na grade de ferramentas de /interno
  // (Rodar Relatório/Validação de PU/BTG, Interno.jsx, antes de abrir uma
  // aba) e nos cards de destino de /escolha (Painel/Área interna/BTG,
  // Escolha.jsx, logo após o login) — mesmo desenho nos dois lugares, por
  // isso um único token em vez de repetir os valores. Reaproveita o mesmo
  // gradiente azul de `cardDistribuicao.hover` (não é um tom próprio).
  // ══════════════════════════════════════════════════════════════════════
  cardEscolha: {
    hover: { c1: palette.azulIndices, c2: palette.azulIndicesEscuro },  // --lev-card-hover-c1/-c2
    icone: { size: 40, radius: 12, background: palette.azulIndices, color: palette.branco, iconSize: 19 },
    logo: { height: 40 },          // altura do slot do logo (BTG) — a imagem em si sai menor (26px) e centralizada aí dentro
    titulo: { color: palette.tintaPrincipal, fontSize: 16 },
    descricao: { color: palette.cinzaMedioClaro, fontSize: 13, lineHeight: 1.5 },
    cta: { color: palette.azulEscuro, fontSize: 13 },
  },
};

// ---- 3) Textos explicativos — cards superiores da Carteira (CarteiraSummaryCard/
// CarteiraHistoricoPreview/Modal, Painel2.jsx). `texto` é o subtítulo/explicação;
// `dica` aparece por último, com ícone — o trecho `{{link}}` dentro da dica é
// substituído por um link de verdade com o rótulo `dicaLinkLabel` (ver InfoDica em
// CarteiraValue.jsx). Sem `{{link}}`, `dicaLinkLabel` é ignorado e a dica sai como
// texto puro. `tituloExtenso` (opcional): no hover e no card ao clicar, substitui o
// título curto do card (ex. "Qtd Integralizada" -> "Quantidade Integralizada") — o
// card compacto do topo continua mostrando o título curto; automático, some se a
// entrada não tiver `tituloExtenso`. ----
export const textosCardCarteira = {
  qtdIntegralizada: {
    texto: 'Quantidade Integralizada atual e série histórica até 6 meses.',
    tituloExtenso: 'Quantidade Integralizada',
    dica: 'Para consultar o calendário completo de integralização, acesse a seção {{link}}.',
    dicaLinkLabel: 'Emissões',
  },
  precoUnitario: {
    texto: 'Preço Unitário atual e série histórica até 6 meses.',
    tituloExtenso: 'Preço Unitário',
    dica: 'Para consultar o Preço Unitário diário, da data de emissão ao vencimento, acesse a seção {{link}}.',
    dicaLinkLabel: 'Emissões',
  },
  valorEmitido: {
    texto: 'Valor Emitido atual e série histórica até 6 meses.'
    //dica: 'Para consultar o calendário completo de integralização, acesse a seção {{link}}.',
    //dicaLinkLabel: 'Emissões',
  },
  saldoDevedor: {
    texto: 'Saldo Devedor atual e série histórica até 6 meses.',
    dica: 'Para consultar o Preço Unitário diário, da data de emissão ao vencimento, acesse a seção {{link}}.',
    dicaLinkLabel: 'Emissões',
  },
};

// ---- 4) Textos do título de introdução de cada aba (TituloIntroAba,
// components/UI/index.jsx) — pra editar o eyebrow ou o título de uma aba, mude
// aqui, nunca solto na JSX. `eyebrow`: rótulo curto acima do título (mesmo
// texto da aba no menu, mas não precisa ser idêntico). `titulo`: a manchete de
// verdade, abaixo do eyebrow — quem chama `TituloIntroAba` pode anexar um
// `sufixo` dinâmico (ex. a data de hoje, na Validação de PU); isso fica de
// fora daqui porque é dado, não texto fixo. Chave = mesmo id da aba usado em
// `activeTab` (Painel2.jsx) ou da página (Interno/ValidacaoPu.jsx). ----
export const textosIntroAba = {
  carteira: {
    eyebrow: 'Carteira',
    titulo: 'Posição Atual da Carteira',
  },
  enquadramento: {
    eyebrow: 'Enquadramento',
    titulo: 'Situação de Enquadramento das Emissões',
  },
  'pu-validacao': {
    eyebrow: 'Validação de PU',
    titulo: 'Validação do Preço Unitário',
  },
  'adm-liberacoes': {
    eyebrow: 'Privadas',
    titulo: 'Liberar Acesso Privado',
  },
  'adm-liberacao-site': {
    eyebrow: 'Site',
    titulo: 'Publicar no Site',
  },
  'adm-comunicados': {
    eyebrow: 'Avisos automáticos',
    titulo: 'Comunicados',
  },
};

export default portalTheme;
